import { authClient } from '@/lib/better-auth-client-actions';
import { isInvalidCredentialAuthError } from '@/lib/auth/email-not-verified';
import { isInlineOnlyAuthError } from '@/lib/auth-inline-errors';
import {
  clearPendingAuthPassword,
  resolvePendingAuthPassword,
} from '@/lib/auth/pending-auth-password';
import { clearStoredReactivationEmail } from '@/lib/reactivation-state';
import { safeSessionStorageRemoveItem } from '@/lib/utils';
import { toast } from 'sonner';
import type { useSignInState } from './use-sign-in-state';
import { pushAuthHandoff } from '@/lib/auth/auth-handoff';
export function createSignInVerificationActions(
  state: ReturnType<typeof useSignInState>
) {
  const {
    router,
    copy,
    handleAuthError,
    returnTo,
    setIsLoading,
    setError,
    pendingEmail,
    otpCode,
    isOtpCodeComplete,
    pendingPassword,
    setPendingPassword,
    goToReactivateStep,
    returnToCredentialsWithPasswordError,
  } = state;

  const handleOtpSubmit = async () => {
    if (!isOtpCodeComplete) {
      setError(copy.enterSixDigitCode);
      return;
    }

    setIsLoading(true);
    setError(null);

    let handedOff = false;
    try {
      // Normalize email to lowercase for case-insensitive handling
      const normalizedEmail = pendingEmail.trim().toLowerCase();

      console.log('[auth:client] otpSubmit start', { email: normalizedEmail });

      // First verify email using the OTP (not sign-in OTP)
      const verifyResult = await authClient.emailOtp.verifyEmail({
        email: normalizedEmail,
        otp: otpCode,
      });

      if (verifyResult.error) {
        console.error('[auth:client] otpSubmit error', {
          error: verifyResult.error,
        });
        const { error: errorMessage } = handleAuthError(verifyResult.error);
        setError(errorMessage || copy.invalidOtp);
        toast.error(errorMessage || copy.invalidOtp);
        setIsLoading(false);
        return;
      }

      const passwordForSignIn = resolvePendingAuthPassword({
        inMemoryPassword: pendingPassword,
        email: normalizedEmail,
      });

      if (!passwordForSignIn) {
        returnToCredentialsWithPasswordError(
          'email-verified-password-required'
        );
        setIsLoading(false);
        return;
      }

      // After verification, sign in with the saved password
      const signInResult = await authClient.signIn.email({
        email: normalizedEmail,
        password: passwordForSignIn,
      });

      if (signInResult.error) {
        const { error: errorMessage } = handleAuthError(signInResult.error);
        if (isInvalidCredentialAuthError(signInResult.error)) {
          returnToCredentialsWithPasswordError('invalid-credentials');
          setIsLoading(false);
          return;
        }
        setError(errorMessage || copy.signInFailed);
        if (!isInlineOnlyAuthError(signInResult.error)) {
          toast.error(errorMessage || copy.signInFailed);
        }
        setIsLoading(false);
        return;
      }

      console.log('[auth:client] otpSubmit success', {
        email: normalizedEmail,
      });
      clearPendingAuthPassword();
      setPendingPassword('');
      clearStoredReactivationEmail(safeSessionStorageRemoveItem);
      if (returnTo) {
        handedOff = pushAuthHandoff(
          router,
          `/auth/signing-in?returnTo=${encodeURIComponent(returnTo)}`
        );
      } else {
        handedOff = pushAuthHandoff(router, '/auth/signing-in');
      }
    } catch (error) {
      console.error('[auth:client] otpSubmit error', { error });
      const { error: errorMessage } = handleAuthError(error);
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      // A handoff keeps the button pending until the page unmounts (#5559).
      if (!handedOff) {
        setIsLoading(false);
      }
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Normalize email to lowercase for case-insensitive handling
      const normalizedEmail = pendingEmail.trim().toLowerCase();

      const result = await authClient.emailOtp.sendVerificationOtp({
        email: normalizedEmail,
        type: 'email-verification',
      });

      if (result.error) {
        const { error: errorMessage } = handleAuthError(result.error);
        setError(errorMessage || copy.failedToResendCode);
        toast.error(errorMessage || copy.failedToResendCodeShort);
      } else {
        toast.success(copy.verificationCodeResent);
      }
    } catch (error) {
      const { error: errorMessage } = handleAuthError(error);
      setError(errorMessage || copy.failedToResendCode);
      toast.error(errorMessage || copy.failedToResendCodeShort);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartReactivation = async () => {
    if (!pendingEmail) {
      setError(copy.enterReactivationEmail);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await authClient.emailOtp.sendVerificationOtp({
        email: pendingEmail,
        type: 'sign-in',
      });

      if (result.error) {
        const { error: errorMessage } = handleAuthError(result.error);
        setError(errorMessage || copy.failedSendReactivationCode);
        toast.error(errorMessage || copy.failedSendReactivationCode);
        return;
      }

      toast.success(copy.reactivationCodeSent);
      goToReactivateStep(pendingEmail, 'otp');
    } catch (reactivationError) {
      const { error: errorMessage } = handleAuthError(reactivationError);
      setError(errorMessage || copy.failedSendReactivationCode);
      toast.error(errorMessage || copy.failedSendReactivationCode);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReactivationSubmit = async () => {
    if (!pendingEmail) {
      setError(copy.enterReactivationEmail);
      return;
    }
    if (!isOtpCodeComplete) {
      setError(copy.enterSixDigitCode);
      return;
    }

    setIsLoading(true);
    setError(null);

    let handedOff = false;
    try {
      const result = await authClient.signIn.emailOtp({
        email: pendingEmail,
        otp: otpCode,
      });

      if (result.error) {
        const { error: errorMessage } = handleAuthError(result.error);
        setError(errorMessage || copy.failedReactivateAccount);
        toast.error(errorMessage || copy.failedReactivateAccount);
        return;
      }

      toast.success(copy.accountReactivated);
      clearStoredReactivationEmail(safeSessionStorageRemoveItem);
      if (returnTo) {
        handedOff = pushAuthHandoff(
          router,
          `/auth/signing-in?returnTo=${encodeURIComponent(returnTo)}`
        );
      } else {
        handedOff = pushAuthHandoff(router, '/auth/signing-in');
      }
    } catch (reactivationError) {
      const { error: errorMessage } = handleAuthError(reactivationError);
      setError(errorMessage || copy.failedReactivateAccount);
      toast.error(errorMessage || copy.failedReactivateAccount);
    } finally {
      // A handoff keeps the button pending until the page unmounts (#5559).
      if (!handedOff) {
        setIsLoading(false);
      }
    }
  };
  return {
    handleOtpSubmit,
    handleResendCode,
    handleStartReactivation,
    handleReactivationSubmit,
  };
}
